import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Snackbar,
  Tab,
  Tabs,
} from '@mui/material';
import { teamApi, PromptConfig } from '@/services/api';
import AIProviderModelSubTab from './ai-subtabs/AIProviderModelSubTab';
import ContextTemplatesSubTab from './ai-subtabs/ContextTemplatesSubTab';
import PromptingSubTab from './ai-subtabs/PromptingSubTab';
import TeamAnalyticsTab from './TeamAnalyticsTab';
import TeamInteractionsTab from './TeamInteractionsTab';
import GitHubIssuesPage from '@/pages/GitHubIssuesPage';
import TeamJiraViewTab from './TeamJiraViewTab';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function TeamChatbotTab() {
  const { teamId } = useParams<{ teamId: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: promptConfig } = useQuery({
    queryKey: ['team-prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId!).then((res) => res.data) as Promise<PromptConfig>,
    enabled: !!teamId,
  });

  if (!teamId) return null;

  const showGithubIssues = !promptConfig || promptConfig.enable_github_issues;
  const showTeamView = !!promptConfig?.enable_jira_team_view;

  // Dynamic indices for conditional tabs
  const githubIssuesIdx = 5;
  const teamViewIdx = githubIssuesIdx + (showGithubIssues ? 1 : 0);

  const handleNotify = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="AI Provider & Model" />
          <Tab label="Context Templates" />
          <Tab label="Prompting" />
          <Tab label="Analytics" />
          <Tab label="Interactions" />
          {showGithubIssues && <Tab label="GitHub Issues" />}
          {showTeamView && <Tab label="Team View" />}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <AIProviderModelSubTab teamId={teamId} onNotify={handleNotify} />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ContextTemplatesSubTab teamId={teamId} onNotify={handleNotify} />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <PromptingSubTab teamId={teamId} onNotify={handleNotify} />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <TeamAnalyticsTab />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <TeamInteractionsTab />
      </TabPanel>
      {showGithubIssues && (
        <TabPanel value={tabValue} index={githubIssuesIdx}>
          <GitHubIssuesPage />
        </TabPanel>
      )}
      {showTeamView && (
        <TabPanel value={tabValue} index={teamViewIdx}>
          <TeamJiraViewTab />
        </TabPanel>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
